import React, { useCallback, useEffect, useState } from 'react';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Activity, AlertTriangle, Clock, LogIn, LogOut, CheckCircle2, CalendarClock, UserX, ShieldAlert, FileWarning,
  KeyRound, MessageSquareWarning, RefreshCw,
} from 'lucide-react';
import { PageHeader, Person, Button, StatusPill } from '../../components';
import { agencyService } from '../../services/agencyService';
import { BookingDetail } from './AgencyBookings';
import {
  CARD, ActivityChips, punctuality, errorMessage, dayLabel, timeRange, clockOf, hoursOf, money, stampOf, longDay,
} from './shared';

/**
 * Agency > Overview — the agency's day at a glance.
 *
 *   Today      who is on shift now and where, who is late (booked, not
 *              clocked in), who starts later, who has finished, who missed
 *   Figures    this week / this month: hours worked and approved, charge,
 *              punctuality
 *   Attention  proof expiring or incomplete, homes asking for changes,
 *              staff who never set a PIN, shifts missed without an outcome
 *   Activity   what happened in the last 7 days
 *
 * Everything comes from the carers' own QR clocking at the homes, so it is as
 * live as the app. Refreshes itself every minute.
 */

const REFRESH_MS = 60 * 1000;

const Tile = ({ label, value, sub, tone }) => (
  <div className={`${CARD} px-5 py-4`}>
    <p className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400">{label}</p>
    <p className={`mt-1 text-[24px] font-bold tabular-nums ${tone === 'warn' ? 'text-amber-600' : tone === 'crit' ? 'text-rose-600' : tone === 'good' ? 'text-emerald-600' : 'text-[#0B1220] dark:text-gray-100'}`}>{value}</p>
    {sub && <p className="text-[12px] text-[#94A3B8]">{sub}</p>}
  </div>
);

const Section = ({ icon: Icon, title, count, tone = 'info', children, empty }) => (
  <div className={`${CARD} p-5 flex flex-col gap-3`}>
    <div className="flex items-center gap-2">
      <Icon className={`w-[18px] h-[18px] ${tone === 'crit' ? 'text-rose-500' : tone === 'warn' ? 'text-amber-500' : tone === 'good' ? 'text-emerald-500' : 'text-blue-600'}`} />
      <p className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">{title}</p>
      {count != null && <span className="ml-auto text-[12px] font-bold text-[#64748B]">{count}</span>}
    </div>
    {count === 0 ? <p className="text-[13px] text-[#94A3B8]">{empty}</p> : children}
  </div>
);

const Row = ({ booking, line, extra, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(booking)}
    className="w-full text-left flex items-start justify-between gap-3 rounded-xl px-3 py-2.5 hover:bg-[#F5F8FC] dark:hover:bg-gray-800/50 transition"
  >
    <Person name={booking.worker?.name} sub={`${booking.home?.name || ''}${booking.unit ? ` · ${booking.unit}` : ''}`} image={booking.worker?.avatar_url} />
    <div className="text-right shrink-0">
      <p className="text-[12.5px] font-semibold text-[#0B1220] dark:text-gray-100">{line}</p>
      {extra && <div className="mt-1 flex justify-end">{extra}</div>}
    </div>
  </button>
);

const FEED_ICON = {
  clock_in: LogIn, clock_out: LogOut, hours_approved: CheckCircle2, confirmed: CheckCircle2,
  did_not_attend: UserX, cancelled: AlertTriangle, rejected: AlertTriangle,
};

const AgencyOverview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await agencyService.dashboard();
      setData(res.data);
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load the overview.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    // The clock alone moves states (due, late), so keep a gentle tick…
    const timer = setInterval(() => load({ quiet: true }), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);
  // …and any change by a care home or a carer's clocking shows at once.
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const b = data?.board || {};
  const w = data?.week || {};
  const m = data?.month || {};
  const att = data?.attention || {};
  const attentionCount = (att.expiring?.length || 0) + (att.incomplete?.length || 0) + (att.changes_requested?.length || 0)
    + (att.no_login?.length || 0) + (att.missed?.length || 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Overview"
        subtitle={data ? `${longDay(data.today)} — where your staff are and what they have worked.` : 'Where your staff are and what they have worked.'}
        actions={<Button variant="secondary" leftIcon={<RefreshCw className="w-4 h-4" />} loading={loading} onClick={() => load()}>Refresh</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Tile label="On shift now" value={b.on_shift?.length ?? '—'} tone={b.on_shift?.length ? 'good' : undefined} />
        <Tile label="Not clocked in" value={b.late?.length ?? '—'} sub="Shift started" tone={b.late?.length ? 'crit' : undefined} />
        <Tile label="Hours this week" value={hoursOf(w.worked_minutes)} sub={`${hoursOf(w.approved_minutes)} approved`} />
        <Tile label="Approved charge this month" value={money(m.approved_charge || 0)} sub={`${hoursOf(m.approved_minutes)} approved`} />
        <Tile label="On time this month" value={m.on_time_rate == null ? '—' : `${m.on_time_rate}%`} sub={`${m.late || 0} late · ${m.no_show || 0} did not attend`} tone={m.on_time_rate != null && m.on_time_rate < 90 ? 'warn' : undefined} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <div className="xl:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-5 content-start">
          <Section icon={Activity} title="On shift now" count={b.on_shift?.length ?? 0} tone="good" empty="Nobody is clocked in right now.">
            {(b.on_shift || []).map((v) => (
              <Row
                key={v.id}
                booking={v}
                onOpen={setOpen}
                line={`In ${clockOf(v.actual?.clock_in)} · until ${v.end_time}`}
                extra={(
                  <span className="flex flex-col items-end gap-1">
                    {punctuality(v.attendance) && <StatusPill tone="warn">{punctuality(v.attendance)}</StatusPill>}
                    <ActivityChips activity={v.activity} empty="Nothing recorded yet" />
                  </span>
                )}
              />
            ))}
          </Section>
          <Section icon={AlertTriangle} title="Not clocked in" count={b.late?.length ?? 0} tone="crit" empty="Everyone due has clocked in.">
            {(b.late || []).map((v) => (
              <Row key={v.id} booking={v} onOpen={setOpen} line={`Booked ${timeRange(v)}`} extra={<StatusPill tone="crit">Started {v.start_time}</StatusPill>} />
            ))}
          </Section>
          <Section icon={CalendarClock} title="Later today" count={b.upcoming?.length ?? 0} empty="No more shifts booked today.">
            {(b.upcoming || []).map((v) => (
              <Row key={v.id} booking={v} onOpen={setOpen} line={timeRange(v)} />
            ))}
          </Section>
          <Section icon={CheckCircle2} title="Finished today" count={(b.finished?.length || 0) + (b.missed?.length || 0)} tone="good" empty="No shifts finished yet today.">
            {(b.finished || []).map((v) => (
              <Row
                key={v.id}
                booking={v}
                onOpen={setOpen}
                line={`${clockOf(v.actual?.clock_in)}–${clockOf(v.actual?.clock_out)} · ${hoursOf(v.actual?.worked_minutes)}`}
                extra={<ActivityChips activity={v.activity} />}
              />
            ))}
            {(b.missed || []).map((v) => (
              <Row key={v.id} booking={v} onOpen={setOpen} line={timeRange(v)} extra={<StatusPill tone="crit">{v.state === 'no_show' ? 'Did not attend' : 'Missed'}</StatusPill>} />
            ))}
          </Section>

          <div className={`${CARD} p-5 lg:col-span-2`}>
            <p className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100 mb-3">This week and this month</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[13px]">
              {[['This week', w], ['This month', m]].map(([label, f]) => (
                <React.Fragment key={label}>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">{label}</p>
                    <p className="text-[#0B1220] dark:text-gray-100"><strong>{f.shifts_worked || 0}</strong> of {f.shifts_booked || 0} shifts worked</p>
                    <p className="text-[#64748B]">{f.staff_working || 0} staff</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">Hours</p>
                    <p className="text-[#0B1220] dark:text-gray-100"><strong>{hoursOf(f.worked_minutes)}</strong> worked of {hoursOf(f.booked_minutes)} booked</p>
                    <p className="text-[#64748B]">{hoursOf(f.approved_minutes)} approved · {f.to_approve || 0} waiting · {money(f.approved_charge || 0)}</p>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="secondary" onClick={() => navigate('/agency/hours?view=staff')}>Hours by staff</Button>
              <Button size="sm" variant="secondary" onClick={() => navigate('/agency/rota')}>Open the rota</Button>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <Section icon={ShieldAlert} title="Needs attention" count={attentionCount} tone="warn" empty="Nothing needs your attention.">
            <ul className="flex flex-col gap-2.5 text-[13px]">
              {(att.missed || []).map((v) => (
                <li key={`m${v.id}`} className="flex items-start gap-2">
                  <UserX className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                  <button type="button" className="text-left hover:underline" onClick={() => setOpen(v)}>
                    <strong>{v.worker?.name}</strong> missed {dayLabel(v.shift_date)} at {v.home?.name} — no clocking recorded
                  </button>
                </li>
              ))}
              {(att.changes_requested || []).map((c) => (
                <li key={`c${c.worker.id}${c.home}`} className="flex items-start gap-2">
                  <MessageSquareWarning className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
                  <button type="button" className="text-left hover:underline" onClick={() => navigate(`/agency/staff?open=${c.worker.id}&tab=homes`)}>
                    <strong>{c.home}</strong> asked for changes to {c.worker.name}{c.note ? `: “${c.note}”` : ''}
                  </button>
                </li>
              ))}
              {(att.expiring || []).map((e) => (
                <li key={`e${e.worker.id}${e.item}`} className="flex items-start gap-2">
                  <FileWarning className={`w-4 h-4 mt-0.5 shrink-0 ${e.expired ? 'text-rose-500' : 'text-amber-500'}`} />
                  <button type="button" className="text-left hover:underline" onClick={() => navigate(`/agency/staff?open=${e.worker.id}&tab=${e.item.startsWith('Training') ? 'training' : 'proof'}`)}>
                    <strong>{e.worker.name}</strong> — {e.item} {e.expired ? 'expired' : 'due'} {dayLabel(e.date)}
                  </button>
                </li>
              ))}
              {(att.incomplete || []).map((i) => (
                <li key={`i${i.worker.id}`} className="flex items-start gap-2">
                  <FileWarning className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <button type="button" className="text-left hover:underline" onClick={() => navigate(`/agency/staff?open=${i.worker.id}&tab=proof`)}>
                    <strong>{i.worker.name}</strong> — {i.issues[0]}{i.issues.length > 1 ? ` (+${i.issues.length - 1})` : ''}
                  </button>
                </li>
              ))}
              {(att.no_login || []).map((u) => (
                <li key={`l${u.id}`} className="flex items-start gap-2">
                  <KeyRound className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                  <button type="button" className="text-left hover:underline" onClick={() => navigate(`/agency/staff?open=${u.id}&tab=details`)}>
                    <strong>{u.name}</strong> hasn't set their app PIN yet
                  </button>
                </li>
              ))}
            </ul>
          </Section>

          <Section icon={Clock} title="Recent activity" count={data?.feed?.length ?? 0} empty="Nothing in the last 7 days.">
            <ul className="flex flex-col gap-3">
              {(data?.feed || []).map((f, i) => {
                const Icon = FEED_ICON[f.kind] || Activity;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={i} className="flex items-start gap-2.5">
                    <Icon className="w-4 h-4 text-[#94A3B8] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[13px] text-[#0B1220] dark:text-gray-100">{f.text}</p>
                      <p className="text-[11.5px] text-[#94A3B8]">{f.sub} · {stampOf(f.at)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
        </div>
      </div>

      <BookingDetail booking={open} onClose={() => setOpen(null)} />
    </div>
  );
};

export default AgencyOverview;
